import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TableRow, { tableRowClasses } from '@mui/material/TableRow';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Card, Link, IconButton, CardContent, useMediaQuery } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { openDocumentViewer } from 'src/store/slices/app/appSlice';
import { ICompanyDocument } from 'src/services/connections/connections.types';

import Iconify from 'src/components/iconify';
import FileThumbnail from 'src/components/file-thumbnail';
import DateDisplay from 'src/components/date/date-display';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import EditProfileDocumentModal from 'src/sections/profile/modals/edit-profile-document-modal';

// ----------------------------------------------------------------------

type Props = {
  row: ICompanyDocument;
  selected: boolean;
  onSaveToLink?: (id: string) => void;
  onDeleteRow?: VoidFunction;
};

export default function DealDocumentTableRow({
  row,
  selected,
  onDeleteRow,
  onSaveToLink
}: Props) {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    document: { public_path, type, name, custom_type_name },
  } = row;

  const dispatch = useAppDispatch();

  const { t } = useTranslate();

  const details = useBoolean();

  const deleteConfirm = useBoolean();

  const popover = usePopover();

  const defaultStyles = {};

  const showEditModal = useBoolean();

  const onDocumentOpen = (item: ICompanyDocument) => {
    dispatch(openDocumentViewer({
      title: name as string,
      fileUrl: item.document.public_path,
    }))
  }

  if (isMobile) {
    return (
      <Card
        sx={{
          m: 2,
          mb: 2,
          ...(selected && {
            boxShadow: theme.customShadows.z20,
            bgcolor: 'background.paper',
          }),
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <FileThumbnail file={public_path} sx={{ width: 40, height: 40 }} />
              <Stack direction="column">
                <TruncatedTextField variant="subtitle1" noWrap text={name} limit={20} mode='characters' />
                <Typography variant="subtitle2" noWrap color={theme.palette.grey[400]}>
                  {t(`enums.document_type.${type}`)} {custom_type_name ? `(${custom_type_name})` : ''}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                LinkComponent={Link}
                href={public_path}
                target="_blank"
                variant="outlined"
                size="small"
              >
                {t('common.open')}
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  deleteConfirm.onTrue();
                }}
              >
                {t('common.delete')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>

        <ConfirmDialog
          open={deleteConfirm.value}
          onClose={deleteConfirm.onFalse}
          title={t('common.delete')}
          content="Are you sure want to delete?"
          action={
            <Button variant="contained" color="error" onClick={onDeleteRow}>
              {t('common.delete')}
            </Button>
          }
        />

        <EditProfileDocumentModal
          open={showEditModal.value}
          onClose={showEditModal.onFalse}
          documentId={row.id}
        />
      </Card>
    );
  }

  return (
    <>
      <TableRow
        selected={selected}
        sx={{
          borderRadius: 2,
          [`&.${tableRowClasses.selected}, &:hover`]: {
            backgroundColor: 'background.paper',
            boxShadow: theme.customShadows.z20,
            transition: theme.transitions.create(['background-color', 'box-shadow'], {
              duration: theme.transitions.duration.shortest,
            }),
            '&:hover': {
              backgroundColor: 'background.paper',
              boxShadow: theme.customShadows.z20,
            },
          },
          [`& .${tableCellClasses.root}`]: {
            ...defaultStyles,
          },
          ...(details.value && {
            [`& .${tableCellClasses.root}`]: {
              ...defaultStyles,
            },
          }),
        }}
      >
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FileThumbnail file={public_path} sx={{ width: 36, height: 36 }} />

            <Stack direction="column">
              <Link onClick={() => onDocumentOpen(row)} style={{ cursor: 'pointer', color: 'inherit' }}>
                <TruncatedTextField variant="subtitle2" noWrap text={name} limit={50} mode='characters' />
              </Link>
              <Typography variant="subtitle2" noWrap color={theme.palette.grey[500]}>
                {t(`enums.document_type.${type}`)} {custom_type_name ? `(${custom_type_name})` : ''}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>

        <TableCell>
          <Button variant="text" onClick={() => onDocumentOpen(row)}>
            {t('common.open')}
          </Button>
        </TableCell>

        <TableCell>
          <DateDisplay date={row.created_at} format="DD/MM/YYYY hh:mm A" variant='body2' />
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <EditProfileDocumentModal
        open={showEditModal.value}
        onClose={showEditModal.onFalse}
        documentId={row.id}
      />

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 160 }}
      >

        {
          onSaveToLink && (
            <MenuItem
              onClick={() => {
                onSaveToLink(row.id);
                popover.onClose();
              }}
            >
              <Iconify icon="lets-icons:folder-send" />
              {t('common.save_to_link')}
            </MenuItem>
          )
        }

        <MenuItem
          onClick={() => {
            deleteConfirm.onTrue();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" color="error.main" />
          {t('common.delete')}
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={deleteConfirm.value}
        onClose={deleteConfirm.onFalse}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            {t('common.delete')}
          </Button>
        }
      />
    </>
  );
}
